import {
  computePosition,
  autoUpdate,
  flip,
  shift,
  offset,
} from "@floating-ui/dom"

import Hook from "../utils/hook"

export default Hook(`ContextMenu`, {
  values: { placement: String },
  targets: [`button`, `menu`],
  actions: [
    {
      target: `button`,
      name: `buttonClick`,
      type: `click`,
    },
  ],

  menuNode: null,
  cleanupFloater: null,

  destroyed() {
    this.close()
  },

  buttonClickAction() {
    if (this.menuNode) this.close()
    else this.open()
  },

  open() {
    const menuId = this.buttonTarget.getAttribute(`aria-controls`)
    if (!menuId) return

    this.menuNode = document.getElementById(menuId)
    if (!this.menuNode) return

    this.attributeSet(`aria-expanded`, `true`, this.buttonTarget)
    this.classRemove(`hidden`, this.menuNode)

    this.cleanupFloater = autoUpdate(this.buttonTarget, this.menuNode, () =>
      computePosition(this.buttonTarget, this.menuNode, {
        placement: this.placementValue || `top`,
        middleware: [flip(), offset(10), shift({ padding: 5 })],
      }).then(({ x, y }) => {
        this.attributeSet(`style`, `top: ${y}px; left: ${x}px`, this.menuNode)
      })
    )

    window.setTimeout(() => this.menuNode.focus(), 50) // timeout is for preventing page scroll as the menu is inserted at the bottom of `body`

    this.menuNode.addEventListener(
      `focusout`,
      (this.menuNodeFocusOut = this.menuNodeFocusOut.bind(this))
    )

    document.addEventListener(
      `click`,
      (this.docClick = this.docClick.bind(this))
    )

    document.addEventListener(
      `keyup`,
      (this.docKeyUp = this.docKeyUp.bind(this))
    )
  },

  close() {
    if (!this.menuNode) return

    document.removeEventListener(`click`, this.docClick)
    document.removeEventListener(`keyup`, this.docKeyUp)
    this.menuNode?.removeEventListener(`focusout`, this.menuNodeFocusOut)

    if (this.buttonTarget)
      this.attributeSet(`aria-expanded`, `false`, this.buttonTarget)

    if (this.menuNode) this.classAdd(`hidden`, this.menuNode)

    this.cleanupFloater?.()
    this.cleanupFloater = null
    this.menuNode = null
    this.buttonTarget?.focus()
  },

  docClick(e) {
    if (!this.buttonTarget.contains(e.target))
      window.setTimeout(this.close.bind(this), 100)
  },

  docKeyUp(e) {
    if (e.key == `Escape`) this.close()
  },

  menuNodeFocusOut(e) {
    if (!e.relatedTarget || e.relatedTarget === this.buttonTarget) return

    if (
      e.relatedTarget?.matches(`div[tabindex="0"]`) ||
      !this.menuNode.contains(e.relatedTarget)
    )
      this.close()
  },
})
